import {
  Box,
  Button,
  DialogContent,
  IconButton,
  ListItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Bot,
  ChevronDown,
  Ellipsis,
  Filter,
  History,
  Home,
  icons,
  Keyboard,
  PanelLeftRightDashed,
  PanelRightDashed,
  PictureInPicture2,
  Puzzle,
} from "lucide-react";
import { t } from "i18next";
import { CustomGrayIconButton } from "./CommonComponents";
import { feature } from "../../services/webConfig";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  filterModalPosState,
  historyModalPosState,
  projectConfigState,
  projectCountInfoState,
  projectInfoState,
  selectedImageState,
} from "../../recoil";
import {
  imageDataState,
  imageViewModeState,
  inspModelUuidState,
  myAIModuleState,
  processImageSrcState,
  scoreMapImageState,
  trainingStateAtom,
  trainUuidState,
  validatingStateAtom,
} from "../../recoil/upgrade";
import { getProjectGeneralInfo, PlayArrowIcon } from "common-lib";
import { LabelModeless } from "../LabelModeless";
import ImageFilter from "../project/ImageFilter";
import { FilterSettings } from "@types";
import { useLayoutStore } from "../../stores/ui.store";
import { getInspModel } from "../../services/modelService";
import { KeyMappingPanel } from "../keyboard/KeyMappingPanel";
import { KeyMappingModal } from "../keyboard/KeyMappingModal";
import FilterModeless from "../FilterModeless";
import { filterDisplayState } from "../../recoil/filterAtoms";
import { getLabelHistory } from "../../services/projectService";
import { formatDate } from "../../utils/utils";
import AIOptionControll from "../train/AIOptionControll";
import AIOptionModal from "./AIOptionModal";

const site = "FUREX";

export default function ProjectHeader() {
  // 증강이 꺼진 납품본 (web-config.json AUGMENTATION.OPTION_VIEW)
  const augView = feature("AUGMENTATION.OPTION_VIEW");
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useRecoilState(projectInfoState);
  const [projectCountInfo, setProjectCountInfo] = useRecoilState(projectCountInfoState);
  const [projectConfig, setProjectConfig] = useRecoilState(projectConfigState);
  const [inspModelUuid, setInspModelUuid] = useRecoilState(inspModelUuidState);

  const [imageViewMode, setImageViewMode] = useRecoilState(imageViewModeState);
  const trainingState = useRecoilValue(trainingStateAtom);
  const validatingState = useRecoilValue(validatingStateAtom);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { setLayoutMode } = useLayoutStore();

  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
  const [openKeyModal, setOpenKeyModal] = useState<boolean>(false);
  const [openHistoryModal, setOpenHistoryModal] = useState<boolean>(false);
  const [openAiOptionModal, setOpenAiOptionModal] = useState<boolean>(false);

  const [filterModalPos, setFilterModalPos] = useRecoilState(filterModalPosState);
  const [filterDisplay, setFilterDisplay] = useRecoilState(filterDisplayState);
  const [historyModalPos, setHistoryModalPos] = useRecoilState(historyModalPosState);
  const [labelHistories, setLabelHistories] = useState<{ label: string; author: string; create_at: string }[]>([]);
  const selectedImage = useRecoilValue(selectedImageState);
  const isAnalysisMain = location.pathname === `/main/project/${id}/analysis`;
  const myAiModule = useRecoilValue(myAIModuleState);

  const iconActions = [
    {
      key: "Filter",
      tooltip: t("ImageFilter"),
      icon: <Filter />,
      onClick: () => {
        setOpenFilterModal(true);
      },
      disabled: false,
    },
    {
      key: "AIOption",
      tooltip: `${t("AIOption")} / 연결된 ai가 없는 경우 설정이 불가합니다.`,
      icon: <Bot />,
      onClick: () => {
        setOpenAiOptionModal(true);
      },
      disabled: myAiModule === null,
    },
    {
      key: "History",
      tooltip: t("LabelHistory"),
      icon: <History />,
      onClick: () => {
        setOpenHistoryModal(true);
      },
      disabled: false,
    },
    {
      key: "Keyboard",
      tooltip: t("KeyMapping"),
      icon: <Keyboard />,
      onClick: () => {
        console.log("단축키 클릭");
        setOpenKeyModal(true);
      },
      disabled: false,
    },
  ];

  const fetchGetProject = async (id: number) => {
    const response = await getProjectGeneralInfo(id);
    console.log(response);

    if (response.project.inspModel_id) {
      try {
        const inspModelData = await getInspModel(response.project.inspModel_id);
        setInspModelUuid(inspModelData.uuid);
      } catch {
        // 연결된 검사모델이 삭제된 경우: 이전 프로젝트 uuid가 남지 않게 비움 (import 시 백엔드가 안내)
        setInspModelUuid(null);
      }
    } else {
      setInspModelUuid(null);
    }
    // 구버전 자동 라벨링 키(autoTrain: boolean, autoTrainCondition) → autoLabeling 키로 변환. 다음 저장 시 새 키로 저장됨
    // config.autoTrain이 객체면 백엔드 스케줄 자동학습 설정이므로 그대로 둔다
    const rawConfig = response.project.config;
    const legacy = typeof rawConfig?.autoTrain === "boolean";
    const { autoTrainCondition, ...restConfig } = rawConfig ?? {};
    if (legacy) delete restConfig.autoTrain;
    const projectConfig = rawConfig && {
      ...restConfig,
      autoLabeling: restConfig.autoLabeling ?? (legacy ? rawConfig.autoTrain : undefined),
      autoLabelingCondition: restConfig.autoLabelingCondition ?? autoTrainCondition,
    };
    console.log(projectConfig);

    setProjectData(response.project);
    setProjectCountInfo(response.project.counts);
    setProjectConfig(projectConfig);
  };

  useEffect(() => {
    fetchGetProject(Number(id));
  }, [id]);

  // 모달이 열려있는 동안 선택 이미지가 바뀌면 이력 갱신 (최근 10개는 백엔드에서 제한)
  useEffect(() => {
    if (!openHistoryModal || !selectedImage.id) {
      setLabelHistories([]);
      return;
    }
    getLabelHistory(selectedImage.id)
      .then((res) => setLabelHistories(res.labelHistories))
      .catch(console.error);
  }, [openHistoryModal, selectedImage.id, selectedImage.label]);

  const handleViewMode = (event: React.MouseEvent<HTMLElement>, value: "original" | "preview" | "scoremap") => {
    if (value !== null) setImageViewMode(value);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpenMenu(false);
      }
    };
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const handleOpenMoreOption = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMoreOption = () => {
    setAnchorEl(null);
  };

  const handleSelectLayout = (mode: "default" | "fold" | "popup") => {
    console.log("선택된 모드:", mode);
    setLayoutMode(mode);
    localStorage.setItem("layoutMode", JSON.stringify(mode));
    handleCloseMoreOption();
  };

  return (
    <>
      <Box
        className="test-page"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          height: "fit-content",
          alignItems: "center",
          px: "10px",
          backgroundColor: "var(--header-bg)",
          borderBottom: "1px solid var(--header-border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              background: "linear-gradient(135deg, var(--brand-gradient-from), var(--brand-gradient-to))",
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              gap: "8px",
              borderRadius: "4px",
              boxShadow: "var(--brand-shadow)",
              backdropFilter: "blur(4px)",
              cursor: "pointer",
            }}
            onClick={() => navigate("/main/projects")}
          >
            <Typography
              sx={{
                fontWeight: 800,
                color: "var(--brand-text)",
                letterSpacing: "1px",
                fontSize: "20px",
              }}
            >
              H
            </Typography>
          </div>
          <span
            style={{
              color: "var(--header-text)",
              fontWeight: 700,
            }}
          >
            {projectData?.project_name || ""}
          </span>
          <Box ref={containerRef} sx={{ position: "relative" }}>
            <IconButton
              id="open-menu-btn"
              sx={{
                color: "var(--header-icon)",
                "&:hover": {
                  backgroundColor: "var(--header-icon-hover)",
                },
              }}
              onClick={() => setOpenMenu(!openMenu)}
            >
              <ChevronDown />
            </IconButton>

            {openMenu && (
              <MenuList>
                <MenuItem
                  onClick={() => {
                    if (projectData?.step === "분석") {
                      navigate(`/main/project/${id}/analysis`);
                    } else {
                      navigate(`/main/project/${id}`);
                    }
                    setOpenMenu(false);
                  }}
                >
                  {t("Labeling")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate(`/main/project/${id}/history`);
                    setOpenMenu(false);
                  }}
                >
                  {t("History")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate(`/main/project/${id}/report`);
                    setOpenMenu(false);
                  }}
                >
                  {t("Report")}
                </MenuItem>
                <div
                  style={{
                    borderTop: "1px solid var(--header-icon)",
                    margin: "4px 0",
                  }}
                />
                <MenuItem
                  onClick={() => {
                    navigate(`/main/project/${id}/setting`);
                    setOpenMenu(false);
                  }}
                >
                  {t("Setting")}
                </MenuItem>
              </MenuList>
            )}
          </Box>
        </div>

        <Box sx={{ display: "flex", gap: "6px", height: "30px" }}>
          {isAnalysisMain ? (
            <Button
              id="analysis-train-btn"
              variant="contained"
              startIcon={<PlayArrowIcon size={13} fill="#fff" />}
              sx={{
                height: "30px",
                minWidth: "fit-content",
                px: 1.75, // 14px
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              {t("RunTraining")}
            </Button>
          ) : (
            <ToggleButtonGroup value={imageViewMode} exclusive onChange={handleViewMode} size="small">
              <ToggleButton value="original">{t("Original")}</ToggleButton>
              <Tooltip title={augView ? "" : t("UnsupportedFeatureMsg", "현재 지원하지 않는 기능입니다.")}>
                <span style={{ display: "inline-flex" }}>
                  <ToggleButton value="preview" disabled={!myAiModule || !augView}>
                    {t("Preview")}
                  </ToggleButton>
                </span>
              </Tooltip>
              <ToggleButton value="scoremap" disabled={!myAiModule}>
                {t("Scoremap")}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {iconActions.map(({ key, icon, tooltip, onClick, disabled }) => (
            <CustomGrayIconButton key={key} icon={icon} tooltip={tooltip} onClick={onClick} disabled={disabled} />
          ))}

          <div
            className="test-page"
            style={{
              backgroundColor: "var(--basic-color)",
              color: "var(--line-icon)",
              borderRadius: "8px",
            }}
          >
            <IconButton sx={{ width: "30px", height: "30px", alignItems: "center" }} onClick={handleOpenMoreOption}>
              <Ellipsis />
            </IconButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleCloseMoreOption}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: "12px",
                  p: 1,
                  backgroundColor: "var(--basic-color)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Box sx={{ display: "flex", width: "fit-content" }}>
                <IconButton onClick={() => handleSelectLayout("default")} title={t("WindowMode")}>
                  <PanelLeftRightDashed />
                </IconButton>

                <IconButton onClick={() => handleSelectLayout("fold")} title={t("Collapse")}>
                  <PanelRightDashed />
                </IconButton>
                <IconButton onClick={() => handleSelectLayout("popup")} title={t("PopupMode")}>
                  <PictureInPicture2 />
                </IconButton>
              </Box>
            </Popover>
          </div>
        </Box>
        <FilterModeless
          open={openFilterModal && filterDisplay === "float"}
          onClose={() => setOpenFilterModal(false)}
          position={filterModalPos}
          setPosition={setFilterModalPos}
          containerRef={containerRef}
          onDock={() => setFilterDisplay("docked")}
        >
          <ImageFilter />
        </FilterModeless>
        <AIOptionModal open={openAiOptionModal} onClose={() => setOpenAiOptionModal(false)} />
        <FilterModeless
          open={openHistoryModal}
          onClose={() => setOpenHistoryModal(false)}
          position={historyModalPos}
          setPosition={setHistoryModalPos}
          containerRef={containerRef}
          title={t("LabelHistory")}
        >
          <Box sx={{ height: "100%", overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t("Label")}</TableCell>
                  <TableCell>{t("User")}</TableCell>
                  <TableCell>{t("UpdatedAt")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labelHistories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t("NoLabelHistory")}
                    </TableCell>
                  </TableRow>
                ) : (
                  labelHistories.map((h, i) => (
                    <TableRow key={i}>
                      <TableCell>{h.label}</TableCell>
                      <TableCell>{h.author}</TableCell>
                      <TableCell>{formatDate(h.create_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </FilterModeless>
        <KeyMappingModal open={openKeyModal} onClose={() => setOpenKeyModal(false)} />
      </Box>
    </>
  );
}

type MenuListProps = { children: React.ReactNode };
type MenuItemProps = { children: React.ReactNode; onClick?: () => void };
type MenuInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
};

export const MenuList = ({ children }: MenuListProps) => {
  return (
    <div
      id="menu-list"
      className="w-48 rounded-xl text-sm shadow-xl p-1 select-none fixed z-[9999]"
      style={{
        backgroundColor: "var(--header-menu-bg)",
        border: "1px solid var(--header-menu-border)",
        color: "var(--header-text)",
      }}
    >
      {children}
    </div>
  );
};

export const MenuItem = ({ children, onClick }: MenuItemProps) => {
  return (
    <div
      onClick={onClick}
      className="px-3 py-1.5 rounded-md cursor-pointer transition-colors"
      style={{
        color: "var(--header-text)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "var(--header-menu-item-hover)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      {children}
    </div>
  );
};

export const MenuInput = ({ value, onChange, placeholder }: MenuInputProps) => {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder || `${t("Search")}...`}
      className="px-3 py-1.5 rounded-md w-full focus:outline-none transition-colors"
      style={{
        backgroundColor: "var(--header-menu-bg)",
        border: "1px solid var(--header-menu-border)",
        color: "var(--header-text)",
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = "0 0 0 2px var(--header-icon)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  );
};
